import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Trash2 } from 'lucide-react';

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="font-heading text-[#f5b400] text-xl md:text-2xl uppercase tracking-wide">
      {title}
    </h2>
    <div className="mt-3 space-y-3 text-sm md:text-[15px] leading-relaxed text-white/85">
      {children}
    </div>
  </section>
);

const Bullet = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-white/85">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

const TermsPage = () => {
  return (
    <div className="pt-28 pb-20 bg-[#0a0608] min-h-screen">
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 font-body">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#f5b400] font-nav text-xs hover:text-white transition-colors"
          data-testid="terms-back"
        >
          <ArrowLeft size={14} /> BACK HOME
        </Link>

        <div className="mt-6">
          <p className="font-nav text-[#f5b400] text-xs tracking-widest inline-flex items-center gap-2">
            <ShieldCheck size={14} /> LEGAL
          </p>
          <h1 className="font-heading section-heading-gold mt-3">PRIVACY POLICY</h1>
          <p className="text-white/60 text-sm mt-3">Effective date: June 3, 2026</p>
        </div>

        <p className="mt-8 text-white/85 text-sm md:text-[15px] leading-relaxed">
          This Privacy Policy describes how <b>New Realm Online</b> (the &quot;Game&quot;)
          handles personal information when you create an account, sign in, play, and
          interact with our services. By using the Game you agree to the practices
          described below. If anything here is unclear, reach out to us at{' '}
          <a href="mailto:newrealmonlinecs@gmail.com" className="text-[#f5b400] underline hover:no-underline">
            newrealmonlinecs@gmail.com
          </a>
          .
        </p>

        <Section title="What we collect">
          <p>To run the Game and keep your progress safe, we store the following:</p>
          <Bullet
            items={[
              'The Google account identifier returned by Google Sign-In',
              'The email address linked to your account',
              'Your in-game display name and player profile',
              'Gameplay progress, character stats, level, and inventory',
              'Equipment and items you own in-game',
              'Firebase Authentication metadata tied to your account',
            ]}
          />
          <p>
            We collect this information only because it is required to deliver core
            functionality such as login, save/restore, and cross-device sync.
          </p>
        </Section>

        <Section title="How we use what we collect">
          <Bullet
            items={[
              'Create and maintain your player account',
              'Authenticate you securely via Google Sign-In',
              'Save and restore your game progress across devices',
              'Persist your character stats, inventory, and other game data',
              'Provide customer support and process account deletion requests',
              'Detect and prevent fraud, abuse, and unauthorized access',
            ]}
          />
        </Section>

        <Section title="Third-party services we rely on">
          <p>
            The Game is built on top of Firebase services provided by Google —
            specifically Firebase Authentication and Cloud Firestore. These services
            store your authentication tokens and your player document on Google&apos;s
            infrastructure.
          </p>
          <p>
            Google processes that data under its own privacy practices. You can read
            them at{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer"
              className="text-[#f5b400] underline hover:no-underline"
            >
              policies.google.com/privacy
            </a>
            .
          </p>
        </Section>

        <Section title="Who we share data with">
          <p>
            We do <b>not</b> sell your personal information to anyone. We share data
            with third parties only when one of the following applies:
          </p>
          <Bullet
            items={[
              'It is strictly necessary to deliver the service (e.g. Firebase/Google for sign-in and storage)',
              'We are compelled by a valid legal request or court order',
              'It is required to protect our rights, our users, or our service',
              'It is needed to investigate fraud, abuse, or a security incident',
            ]}
          />
        </Section>

        <Section title="Security">
          <p>
            We rely on Firebase and standard industry safeguards to keep your account
            data protected. No system is ever 100% secure, but we treat your data with
            care and respond promptly if anything looks wrong.
          </p>
        </Section>

        <Section title="How long we keep your data">
          <p>
            We retain your account and game data while your account is active or while
            we need it to provide the service to you. If you request deletion, we
            remove your Firebase Authentication user record and your associated player
            data, unless a small subset must be retained for legal, accounting,
            anti-fraud, or regulatory reasons.
          </p>
        </Section>

        <Section title="Deleting your account">
          <p>
            You can ask us to permanently delete your account and the data tied to it.
            The fastest way is to use the in-Game{' '}
            <b>Delete Account</b> option, or to submit our web form:
          </p>
          <Link
            to="/delete-account"
            data-testid="terms-link-delete-account"
            className="inline-flex items-center gap-2 mt-2 bg-[#f5b400] hover:bg-[#ffc630] text-black font-nav text-xs px-5 py-2.5 rounded-md transition-colors"
          >
            <Trash2 size={14} /> ACCOUNT DELETION REQUEST
          </Link>
          <p>
            You can also email{' '}
            <a
              href="mailto:newrealmonlinecs@gmail.com"
              className="text-[#f5b400] underline hover:no-underline"
            >
              newrealmonlinecs@gmail.com
            </a>{' '}
            from the address you used to sign in. Please include your in-game display
            name so we can locate your character. Deletion is permanent.
          </p>
        </Section>

        <Section title="Children">
          <p>
            New Realm Online is not aimed at children under the age of 13. We do not
            knowingly collect personal information from children under 13. If you
            believe we may hold data for a child, please email us and we will remove
            it.
          </p>
        </Section>

        <Section title="Your choices">
          <Bullet
            items={[
              'Stop playing the Game at any time',
              'Remove the app from your device',
              'Use the in-Game Delete Account option',
              'Send us a deletion request via the web form linked above',
              'Email us with any privacy questions',
            ]}
          />
        </Section>

        <Section title="Updates to this policy">
          <p>
            If we materially change how we handle data, we will post the new policy on
            this page and update the effective date at the top. Continued use of the
            Game after a change means you accept the updated policy.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            For privacy questions or account/data deletion requests:{' '}
            <a
              href="mailto:newrealmonlinecs@gmail.com"
              className="text-[#f5b400] underline hover:no-underline"
            >
              newrealmonlinecs@gmail.com
            </a>
          </p>
        </Section>

        <div className="mt-12 pt-6 border-t border-white/10 text-white/55 text-xs">
          App: New Realm Online · © 2024-2026 newrealmonline.com
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
